<template>
    <vx-modal size="md" padding="standard" :visible="!!model && !token" @update:visible="model = null">
        <template v-slot:header> {{ model.id ? 'Update' : 'Create' }} API Token </template>

        <form @submit.prevent="submit()" id="token-form">
            <div>
                <vx-label>Token Name</vx-label>
                <vx-input
                    type="text"
                    ref="inputRef"
                    size="lg"
                    name="name"
                    v-model="model.name"
                    :error="displayFirstError(validate.name) || displayFirstError(errors.name)"
                />
            </div>
        </form>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 lg:vx-flex-row">
                <vx-button
                    @click.prevent="model = null"
                    tabindex="1"
                    type="button"
                    class="vx-grow lg:vx-w-20 lg:vx-grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    :loading="submitting"
                    form="token-form"
                    tabindex="0"
                    data-test="confirm-button"
                    type="submit"
                    class="vx-grow"
                    size="lg"
                    color="primary"
                >
                    {{ model.id ? 'Update' : 'Create' }} API Token
                </vx-button>
            </div>
        </template>
    </vx-modal>

    <vx-modal
        class="vx-max-w-lg"
        padding="standard"
        :visible="!!token"
        @update:visible="
            token = null;
            model = null;
        "
    >
        <template v-slot:header> API Token Created </template>

        <template v-slot:subheader>
            Your API Token has been created. Store it somewhere safe. You won't be able to see the token again.
        </template>

        <vx-alert color="info" :closeable="false">
            Here is your new API Token.
            <strong>This is the only time the token will ever be displayed, so be sure not to lose it!</strong>
            You may revoke the token at any time from your API settings.
        </vx-alert>
        <vx-popover hover arrow placement="top">
            <div
                @click.prevent="copyToken()"
                data-test="copy-token"
                class="vx-font-mono vx-break-all vx-cursor-pointer vx-p-4 vx-bg-white vx-rounded-sm vx-mt-4"
            >
                {{ token }} <font-awesome-icon :icon="faCopy"></font-awesome-icon>
            </div>
            <template v-slot:content> Click to Copy </template>
        </vx-popover>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 lg:vx-flex-row">
                <vx-button
                    @click.prevent="
                        model = null;
                        token = null;
                    "
                    tabindex="1"
                    type="button"
                    class="vx-grow lg:vx-w-20 lg:vx-grow-0"
                    size="lg"
                    color="muted"
                >
                    Close
                </vx-button>
                <vx-button @click.prevent="copyToken()" type="button" size="lg" block color="primary">
                    Copy Token <font-awesome-icon :icon="faCopy"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue';
import { VxAlert, VxLabel, VxInput, VxModal, VxButton, VxPopover } from '@voxie/frontend-components';
import { displayFirstError } from '~/utils/validation';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { useToasted } from '~/composables/useToasted';
import axios from '~/services/axios';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { useCopyText } from '~/composables/useCopyText';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const toasted = useToasted();

const model = defineModel({
    type: Object,
    required: false,
});

const submitting = ref(false);
const success = ref(false);
const error = ref('');
const errors = ref({});
const token = ref(null);

const { copyText } = useCopyText();

const copyToken = () => {
    copyText(token.value);
};

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(255) },
    },
    model,
    {
        $autoDirty: true,
    }
);

const inputRef = ref();
watch(
    () => !!model.value,
    async (showModal) => {
        validate.value.$reset();
        await nextTick();
        if (showModal) {
            inputRef.value.field?.focus?.();
        }
    }
);

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validate.value.$validate();

    if (!isValid) {
        toasted.error('Please check form data.');
        return;
    }

    success.value = false;
    errors.value = {};
    error.value = '';
    submitting.value = true;

    try {
        if (model.value.id) {
            await axios.put(`/settings/api/token/${model.value.id}`, model.value);
            model.value = null;
        } else {
            const response = await axios.post(`/settings/api/token`, model.value);
            token.value = response.data.token;
        }

        Bus.$emit('updateTokens');

        validate.value.$reset();
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.';
        } else {
            if (e.response.status === HTTP_UNPROCESSABLE_ENTITY) {
                errors.value = e.response.data.errors;
            }
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
    } finally {
        submitting.value = false;
    }
};
</script>
